import { useEffect } from "react";
import type { Meta } from "../../shared/seo";

// Applies metadata on client-side navigation. The first load is server-rendered,
// so this is an update path only — and it must produce the same head the server
// does, which is guaranteed by both sides consuming metaFor().

export function useMeta(meta: Meta | null) {
  useEffect(() => {
    if (!meta) return;
    document.title = meta.title;
    upsertMeta("name", "description", meta.description);
    upsertMeta("property", "og:title", meta.title);
    upsertMeta("property", "og:description", meta.description);
    upsertMeta("property", "og:type", meta.ogType);
    upsertMeta("property", "og:url", meta.canonical);

    if (meta.image) {
      upsertMeta("property", "og:image", meta.image);
      upsertMeta("name", "twitter:image", meta.image);
    }

    if (meta.robots) upsertMeta("name", "robots", meta.robots);
    else document.head.querySelector('meta[name="robots"]')?.remove();

    let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = meta.canonical;

    document.getElementById("__CC_JSONLD__")?.remove();
    if (meta.jsonLd) {
      const script = document.createElement("script");
      script.id = "__CC_JSONLD__";
      script.type = "application/ld+json";
      script.textContent = JSON.stringify(meta.jsonLd);
      document.head.appendChild(script);
    }

    // The server emits breadcrumbs as a second block, so the update path has to
    // replace that one too or a soft navigation leaves the previous page's
    // trail in the head.
    document.getElementById("__CC_CRUMBS__")?.remove();
    if (meta.breadcrumbs?.length) {
      const base = new URL(meta.canonical).origin;
      const script = document.createElement("script");
      script.id = "__CC_CRUMBS__";
      script.type = "application/ld+json";
      script.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [{ name: "Home", url: `${base}/` }, ...meta.breadcrumbs].map((c, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: c.name,
          item: c.url,
        })),
      });
      document.head.appendChild(script);
    }
  }, [meta]);
}

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}
